import { Clock, Link as LinkIcon, PowerOff } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface StatusBreakdown {
  online: number;
  inProgress: number;
  offline: number;
  total: number;
  activeTunnels: number;
  scheduledRestarts: number;
  autoShutdownDisabled?: number;
}

interface EnvironmentStatsCardProps {
  environmentType: 'production' | 'non-production' | 'internal';
  stats: StatusBreakdown;
  onClick: () => void;
  isActive: boolean;
}

const EnvironmentStatsCard = ({
  environmentType,
  stats,
  onClick,
  isActive,
}: EnvironmentStatsCardProps) => {
  // Get badge styling based on environment type (matching columns.tsx)
  const getBadgeStyles = () => {
    switch (environmentType) {
      case 'production':
        return 'bg-red-600 text-white border-red-700';
      case 'non-production':
        return 'bg-blue-600 text-white border-blue-700';
      case 'internal':
        return 'bg-orange-600 text-white border-orange-700';
    }
  };

  // Get display label
  const getLabel = () => {
    switch (environmentType) {
      case 'production':
        return 'Production';
      case 'non-production':
        return 'Non-Production';
      case 'internal':
        return 'Internal';
    }
  };

  // Calculate percentages
  const getPercentage = (count: number) => {
    if (stats.total === 0) return '0.0';
    return ((count / stats.total) * 100).toFixed(1);
  };

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
        isActive ? 'ring-2 ring-primary shadow-lg' : 'shadow-md'
      }`}
      onClick={onClick}
    >
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between mb-2'>
          <Badge variant='outline' className={getBadgeStyles()}>
            {getLabel()}
          </Badge>
          <span className='text-sm font-semibold text-muted-foreground'>{stats.total} Total</span>
        </div>

        {/* Badge Indicators */}
        <TooltipProvider>
          <div className='flex items-center gap-2 flex-wrap'>
            {stats.activeTunnels > 0 && (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <div>
                    <Badge
                      variant='secondary'
                      className='text-xs flex items-center gap-1 bg-green-100 text-green-700 border-green-300 hover:bg-green-200 cursor-help'
                    >
                      <LinkIcon className='h-3 w-3' />
                      {stats.activeTunnels}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='text-xs'>Active Remote Debug Tunnels</p>
                </TooltipContent>
              </Tooltip>
            )}

            {stats.scheduledRestarts > 0 && (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <div>
                    <Badge
                      variant='secondary'
                      className='text-xs flex items-center gap-1 bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200 cursor-help'
                    >
                      <Clock className='h-3 w-3' />
                      {stats.scheduledRestarts}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='text-xs'>Active Scheduled Restarts</p>
                </TooltipContent>
              </Tooltip>
            )}

            {environmentType === 'internal' &&
              stats.autoShutdownDisabled !== undefined &&
              stats.autoShutdownDisabled > 0 && (
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <div>
                      <Badge
                        variant='secondary'
                        className='text-xs flex items-center gap-1 bg-red-100 text-red-700 border-red-300 hover:bg-red-200 cursor-help'
                      >
                        <PowerOff className='h-3 w-3' />
                        {stats.autoShutdownDisabled}
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='text-xs'>Support Environments with Auto-Shutdown Disabled</p>
                  </TooltipContent>
                </Tooltip>
              )}
          </div>
        </TooltipProvider>
      </CardHeader>
      <CardContent className='space-y-3'>
        {/* Online Status */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='h-3 w-3 rounded-full bg-green-500' />
            <span className='text-sm font-medium'>Online</span>
          </div>
          <div className='text-right'>
            <span className='text-lg font-bold'>{stats.online}</span>
            <span className='ml-2 text-sm text-muted-foreground'>
              ({getPercentage(stats.online)}%)
            </span>
          </div>
        </div>

        {/* In Progress Status */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='h-3 w-3 rounded-full bg-yellow-500 animate-pulse' />
            <span className='text-sm font-medium'>In Progress</span>
          </div>
          <div className='text-right'>
            <span className='text-lg font-bold'>{stats.inProgress}</span>
            <span className='ml-2 text-sm text-muted-foreground'>
              ({getPercentage(stats.inProgress)}%)
            </span>
          </div>
        </div>

        {/* Offline Status */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='h-3 w-3 rounded-full bg-gray-400' />
            <span className='text-sm font-medium'>Offline</span>
          </div>
          <div className='text-right'>
            <span className='text-lg font-bold'>{stats.offline}</span>
            <span className='ml-2 text-sm text-muted-foreground'>
              ({getPercentage(stats.offline)}%)
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnvironmentStatsCard;
